import { useEffect, useMemo, useState } from "react";
import { ajustarInventario } from "../../../services/usuarios_tienda/inventario";

function InventoryAdjustModal({ isOpen, onClose, product, onSuccess }) {
  const [nuevaStr, setNuevaStr] = useState("0");
  const [observacion, setObservacion] = useState("");
  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const getNiceErrorMessage = (error, fallback = "Error al ajustar inventario.") => {
    const msg =
      error?.response?.data?.message ||
      error?.message ||
      fallback;

    const clean = String(msg).replace(/\s+/g, " ").trim();
    return clean.length > 240 ? clean.slice(0, 240) + "…" : clean;
  };

  const title = useMemo(() => {
    if (!product) return "Ajustar inventario";
    const part = product.no_parte ? ` • ${product.no_parte}` : "";
    return `${product.nombre}${part}`;
  }, [product]);

  useEffect(() => {
    if (!isOpen) return;

    setNuevaStr(String(Number(product?.cantidad_disponible ?? 0)));
    setObservacion("");
    setLoading(false);
    setAlert(null);
  }, [isOpen, product?.producto_id, product?.cantidad_disponible]);

  useEffect(() => {
    if (!isOpen) return;

    const handler = (e) => {
      if (e.key === "Escape") onClose?.();
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [isOpen, onClose]);

  const stockActual = Number(product?.cantidad_disponible ?? 0);

  const nuevaCantidad = useMemo(() => {
    const n = Number(nuevaStr);
    return Number.isFinite(n) && Number.isInteger(n) && n >= 0 ? n : null;
  }, [nuevaStr]);

  if (!isOpen || !product) return null;

  const validate = () => {
    if (nuevaCantidad === null) {
      return "Ingresa una cantidad válida (entero mayor o igual a 0).";
    }
    if (nuevaCantidad === stockActual) {
      return "La nueva cantidad es igual al stock actual; no hay cambios que aplicar.";
    }
    if (!observacion.trim()) {
      return "La razón del ajuste es obligatoria.";
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setAlert(null);

    const validationError = validate();
    if (validationError) {
      setAlert({ type: "error", message: validationError });
      return;
    }

    try {
      setLoading(true);

      // El backend trabaja con un delta (+/−); calculamos la diferencia
      // entre la nueva cantidad deseada y el stock actual.
      const delta = nuevaCantidad - stockActual;

      await ajustarInventario({
        producto_id: product.producto_id,
        cantidad: delta,
        observacion: observacion.trim(),
      });

      setAlert({ type: "success", message: "Inventario ajustado correctamente." });
      onSuccess?.();

      onClose?.();
    } catch (err) {
      console.error("Error al ajustar inventario:", err);
      setAlert({ type: "error", message: getNiceErrorMessage(err) });
    } finally {
      setLoading(false);
    }
  };

  const closeIfBackdrop = (e) => {
    if (e.target === e.currentTarget && !loading) onClose?.();
  };

  return (
    <div className="adc-modal-backdrop" onMouseDown={closeIfBackdrop}>
      <div className="adc-modal" onMouseDown={(e) => e.stopPropagation()}>
        <h3 className="adc-modal-title">Ajustar inventario</h3>

        <p className="adc-modal-subtitle">{title}</p>

        <p className="adc-modal-stock">
          Stock actual: <strong>{stockActual}</strong>
        </p>

        {alert && (
          <div
            className={`adc-alert ${alert.type === "error" ? "adc-alert--error" : "adc-alert--success"}`}
          >
            <span>{alert.message}</span>
            <button
              type="button"
              className="adc-alert-x"
              onClick={() => setAlert(null)}
              disabled={loading}
              aria-label="Cerrar alerta"
            >
              ✕
            </button>
          </div>
        )}

        <form onSubmit={handleSubmit} className="adc-modal-form">
          <label>
            Nueva cantidad disponible
            <input
              type="number"
              step="1"
              min="0"
              value={nuevaStr}
              onChange={(e) => setNuevaStr(e.target.value)}
              placeholder="Ej: 0, 10, 25"
              required
              disabled={loading}
            />
          </label>
          <p className="adc-modal-help">
            Escribe la cantidad final que quieres dejar disponible. Coloca <strong>0</strong>{" "}
            para dejar el producto sin stock.
          </p>

          <label>
            Razón *
            <textarea
              value={observacion}
              onChange={(e) => setObservacion(e.target.value)}
              placeholder="Explica por qué es necesario este ajuste"
              required
              disabled={loading}
            />
          </label>

          <div className="adc-modal-actions">
            <button
              type="button"
              className="btn-secondary"
              onClick={onClose}
              disabled={loading}
            >
              Cancelar
            </button>

            <button type="submit" className="btn-warning" disabled={loading}>
              {loading ? "Guardando..." : "Aplicar ajuste"}
            </button>
          </div>
        </form>
      </div>

      <style>{`
        .adc-modal-stock{
          margin: 6px 0 0;
          font-size: 13px;
          color: #475569;
          font-weight: 600;
        }
        .adc-modal-stock strong{ color: #0f172a; }
        .adc-modal-help{
          margin: 6px 0 0;
          font-size: 12px;
          color: #64748b;
          line-height: 1.45;
        }
        .adc-modal-help strong{ color: #b91c1c; }
        .adc-alert{
          margin: 12px 0 0;
          padding: 10px 12px;
          border-radius: 10px;
          border: 1px solid;
          display:flex;
          align-items:center;
          justify-content:space-between;
          gap: 12px;
          font-weight: 700;
          font-size: 13px;
        }
        .adc-alert--error{
          background:#FEF2F2;
          border-color:#FECACA;
          color:#7F1D1D;
        }
        .adc-alert--success{
          background:#ECFDF5;
          border-color:#A7F3D0;
          color:#065F46;
        }
        .adc-alert-x{
          border:none;
          background:transparent;
          cursor:pointer;
          font-size: 16px;
          opacity:.7;
        }
        .adc-alert-x:hover{ opacity: 1; }
      `}</style>
    </div>
  );
}

export default InventoryAdjustModal;
